import {
  QuestionData,
  FormFieldValue,
} from '@/features/submission_forms/types';

/**
 * Whether a form value counts as an answer. `false` and `0` are answers;
 * only null/undefined/'' and empty arrays are not.
 */
export const isAnsweredValue = (value: FormFieldValue | undefined): boolean => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/**
 * The one definition of "this answer equals this option / condition value".
 * Case- and whitespace-insensitive, mirroring the server's
 * VisibilityCalculator#condition_values_equal?. OV2-imported templates store
 * depends_on_value in a different case than the answer option value (condition
 * "yes" vs stored "Yes"); a strict === would hide the dependent question.
 * OXX-1295: a trailing colon is ignored too (stored "Other:" vs condition "Other").
 * The multi-select field ticks its checkboxes with it too (OV-1527), so a box
 * is ticked exactly when a condition on that option would see it as chosen.
 */
export const normalizeAnswerText = (v: unknown): string =>
  String(v).trim().replace(/\s*:$/, '').toLowerCase();

/**
 * The single place a condition reads the parent's answer. Repeatable groups
 * (OV-1480) add the copy number here, and in
 * `Submissions::VisibilityCalculator.answer_value_for`.
 */
export const answerValueFor = (
  questionSlug: string,
  formValues: Record<string, FormFieldValue> | undefined
): FormFieldValue | undefined => formValues?.[questionSlug];

/**
 * Evaluates whether a question's own dependency condition is satisfied by the
 * current form values. Does NOT consider whether the parent question is itself
 * visible — use `buildVisibilityMap` for transitive visibility.
 *
 * Comparisons coerce both sides to strings so a boolean checkbox parent (`true`)
 * matches a `depends_on.value` of `'true'`, mirroring the backend's `to_s`
 * comparison in `Submissions::VisibilityCalculator#condition_values_equal?`.
 */
export const isQuestionVisible = (
  question: QuestionData,
  formValues: Record<string, FormFieldValue> | undefined
): boolean => {
  if (!question.depends_on) return true;

  const { question_slug, op, value: expectedValue } = question.depends_on;
  const actualValue = answerValueFor(question_slug, formValues);

  // An unanswered parent — including a multi-select with nothing ticked — hides
  // the dependent under both ops, matching VisibilityCalculator. Without this,
  // '!=' on an empty selection would show it ([] does not contain the value).
  if (!isAnsweredValue(actualValue)) return false;

  const expected = normalizeAnswerText(expectedValue);
  const matches = Array.isArray(actualValue)
    ? actualValue.map(normalizeAnswerText).includes(expected)
    : normalizeAnswerText(actualValue) === expected;

  // The server only stores the ops in QuestionBehavior::SUPPORTED_DEPENDS_ON_OPS.
  // On a multi-select (array) parent, '=' means "contains" and '!=' means
  // "does not contain" — the same as VisibilityCalculator. Any other op that
  // reaches the renderer hides the question, as VisibilityCalculator does.
  // (The tree-save paths drop such a condition entirely instead; see OV-1334.)
  switch (op) {
    case '=':
      return matches;
    case '!=':
      return !matches;
    default:
      return false;
  }
};

/**
 * Returns the question plus every ancestor it (transitively) depends on, from
 * the question itself up to the root of the chain. Stops at a dependency slug
 * that isn't in `questionsBySlug`, and on cycles.
 */
export const getDependencyChain = (
  question: QuestionData,
  questionsBySlug: ReadonlyMap<string, QuestionData>
): QuestionData[] => {
  const chain: QuestionData[] = [];
  const seen = new Set<string>();
  let current: QuestionData | undefined = question;
  while (current && !seen.has(current.slug)) {
    seen.add(current.slug);
    chain.push(current);
    current = current.depends_on
      ? questionsBySlug.get(current.depends_on.question_slug)
      : undefined;
  }
  return chain;
};

/**
 * Transitive visibility for a single question: every link in its dependency
 * chain must have its own condition met.
 */
export const isQuestionVisibleTransitive = (
  question: QuestionData,
  formValues: Record<string, FormFieldValue> | undefined,
  questionsBySlug: ReadonlyMap<string, QuestionData>
): boolean => {
  const chain = getDependencyChain(question, questionsBySlug);
  const last = chain[chain.length - 1];
  const parentOfLast = last.depends_on
    ? questionsBySlug.get(last.depends_on.question_slug)
    : undefined;
  // Chain ended because it looped back on itself — cyclic data → hidden
  if (parentOfLast && chain.includes(parentOfLast)) return false;
  return chain.every(q => isQuestionVisible(q, formValues));
};

/**
 * Resolves visibility for every question TRANSITIVELY: a question is visible
 * only when its own condition is met AND the question it depends on is itself
 * visible. This is what hides a whole branch when the user un-selects the
 * parent option that revealed it, even if intermediate questions still hold
 * stale values.
 *
 * Questions whose dependency slug is not present in `questions` fall back to
 * evaluating only their own condition. Cyclic dependencies (a data error) are
 * treated as hidden.
 */
export const buildVisibilityMap = (
  questions: QuestionData[],
  formValues: Record<string, FormFieldValue> | undefined
): Map<string, boolean> => {
  const questionsBySlug = new Map(questions.map(q => [q.slug, q]));
  const memo = new Map<string, boolean>();

  const resolve = (question: QuestionData, visiting: Set<string>): boolean => {
    const cached = memo.get(question.slug);
    if (cached !== undefined) return cached;
    if (!question.depends_on) {
      memo.set(question.slug, true);
      return true;
    }
    if (visiting.has(question.slug)) {
      memo.set(question.slug, false);
      return false;
    }
    visiting.add(question.slug);

    const parent = questionsBySlug.get(question.depends_on.question_slug);
    const parentVisible = parent ? resolve(parent, visiting) : true;
    const visible = parentVisible && isQuestionVisible(question, formValues);

    memo.set(question.slug, visible);
    return visible;
  };

  questions.forEach(q => resolve(q, new Set()));
  return memo;
};
